import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { functions, db } from './firebase';

/**
 * Utilities around banning/unbanning customer phone numbers using Cloud Functions.
 * Server functions deployed:
 * - banPhone({ phone: "+2126XXXXXXXX", reason?: string })
 * - unbanPhone({ phone: "+2126XXXXXXXX" })
 * Blocking functions will prevent signup/signin for banned numbers.
 */

// Ensure Moroccan E.164: +212[6|7]XXXXXXXX
export function toE164Morocco(input: string | undefined | null): string | null {
  if (!input) return null;
  let p = input.trim();
  // Already correct
  if (/^\+212[67]\d{8}$/.test(p)) return p;

  // Remove spaces and non-digits except +
  p = p.replace(/[^\d+]/g, '');

  if (p.startsWith('+212')) {
    // Normalize any trailing form
    const rest = p.substring(4);
    // If leading 0 after +212, drop it
    const cleaned = rest.startsWith('0') ? rest.substring(1) : rest;
    const candidate = `+212${cleaned}`;
    return /^\+212[67]\d{8}$/.test(candidate) ? candidate : null;
  }

  if (p.startsWith('212')) {
    const rest = p.substring(3);
    const candidate = `+212${rest}`;
    return /^\+212[67]\d{8}$/.test(candidate) ? candidate : null;
  }

  if (p.startsWith('0')) {
    const rest = p.substring(1);
    const candidate = `+212${rest}`;
    return /^\+212[67]\d{8}$/.test(candidate) ? candidate : null;
  }

  // If 9 digits and starts with 6 or 7, assume Morocco mobile
  if (/^[67]\d{8}$/.test(p)) {
    const candidate = `+212${p}`;
    return /^\+212[67]\d{8}$/.test(candidate) ? candidate : null;
  }

  return null;
}

async function callBanPhone(phoneE164: string, reason?: string): Promise<void> {
  const callable = httpsCallable(functions, 'banPhone');
  await callable({ phone: phoneE164, reason: reason ?? 'Banni via admin' });
}

async function callUnbanPhone(phoneE164: string): Promise<void> {
  const callable = httpsCallable(functions, 'unbanPhone');
  await callable({ phone: phoneE164 });
}

/**
 * Check if a phone is banned by reading Firestore bannedPhones/{phoneE164}.
 * Note: This requires Firestore rules to allow admin read access to this collection.
 * If rules deny read, you can hide badges and rely on optimistic UI after ban/unban actions.
 */
async function isPhoneBanned(phoneE164: string): Promise<boolean> {
  try {
    const ref = doc(db, 'bannedPhones', phoneE164);
    const snap = await getDoc(ref);
    return snap.exists();
  } catch {
    // If read is denied, assume not banned (UI can still trigger ban/unban)
    return false;
  }
}

async function getBannedPhones(phoneE164s: string[]): Promise<Set<string>> {
  const bannedPhones = new Set<string>();
  const uniquePhones = Array.from(
    new Set(phoneE164s.filter((phone) => phone && phone.trim().length > 0))
  );

  if (uniquePhones.length === 0) {
    return bannedPhones;
  }

  try {
    const chunkSize = 30;
    for (let index = 0; index < uniquePhones.length; index += chunkSize) {
      const chunk = uniquePhones.slice(index, index + chunkSize);
      const bannedPhonesQuery = query(
        collection(db, 'bannedPhones'),
        where(documentId(), 'in', chunk)
      );
      const snapshot = await getDocs(bannedPhonesQuery);
      snapshot.docs.forEach((docSnap) => bannedPhones.add(docSnap.id));
    }
  } catch {
    return new Set<string>();
  }

  return bannedPhones;
}

const banService = {
  toE164Morocco,
  banPhone: callBanPhone,
  unbanPhone: callUnbanPhone,
  isPhoneBanned,
  getBannedPhones,
};

export default banService;
